import useAxios, { api } from '@hooks/useFetch';
import { components, paths } from '@api/api';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { addNotification } from '@actions/notifications.actions';
import { downloadFileFromBlobResponse } from '@core/utils/file';

const TOP_TRENDS_EXPORT_ENDPOINT: keyof paths = '/TopTrendMainExport';
const TOP_TRENDS_PPT_EXPORT_ENDPOINT: keyof paths = '/TopTrendPptExport';

export type TopTrendsExportRequest = components['schemas']['Infrastructure.DTOs.TopTrendExportFilter'];

export default function useTopTrendsExport() {
    const dispatch = useDispatch();
    const [isExporting, setIsExporting] = useState(false);

    const pushNotification = useCallback(
        (notification) =>
            dispatch(addNotification(notification)),
        [dispatch]
    );

    const {
        doFetch: doExportXls,
        cancel: cancelXls,
    } = useAxios<string, TopTrendsExportRequest>({
        method: 'post',
        url: TOP_TRENDS_EXPORT_ENDPOINT,
        lazy: true,
    });

    const {
        doFetch: doExportPpt,
        cancel: cancelPpt,
    } = useAxios<string, TopTrendsExportRequest>({
        method: 'post',
        url: TOP_TRENDS_PPT_EXPORT_ENDPOINT,
        lazy: true,
    });

    const downloadXls = useCallback(
        async (requestData: TopTrendsExportRequest) => {
            const notifyId = Date.now();
            const notifyDuration = 2000;

            setIsExporting(true);
            pushNotification({
                id: notifyId,
                text: 'Downloading Excel...',
            });

            try {
                const response = await doExportXls({ data: requestData });
                const token = response?.data;

                if (token) {
                    const blobResponse = await api.get(
                        `${TOP_TRENDS_EXPORT_ENDPOINT}?key=${token}`,
                        { responseType: 'blob' }
                    );
                    await downloadFileFromBlobResponse(blobResponse);
                    pushNotification({
                        text: 'Download was successful',
                        duration: notifyDuration,
                        id: notifyId,
                    });
                }
            } catch (err) {
                pushNotification({
                    text: 'Something went wrong',
                    id: notifyId,
                    error: true,
                    duration: notifyDuration,
                });
            } finally {
                setIsExporting(false);
            }
        },
        [doExportXls, pushNotification]
    );

    const downloadPpt = useCallback(
        async (requestData: TopTrendsExportRequest) => {
            const notifyId = Date.now();
            const notifyDuration = 2000;

            setIsExporting(true);
            pushNotification({
                id: notifyId,
                text: 'Downloading PowerPoint...',
            });

            try {
                const response = await doExportPpt({ data: requestData });
                const token = response?.data;

                if (token) {
                    const blobResponse = await api.get(
                        `${TOP_TRENDS_PPT_EXPORT_ENDPOINT}?key=${token}`,
                        { responseType: 'blob' }
                    );
                    await downloadFileFromBlobResponse(blobResponse);
                    pushNotification({
                        text: 'Download was successful',
                        duration: notifyDuration,
                        id: notifyId,
                    });
                }
            } catch (err) {
                pushNotification({
                    text: 'Something went wrong',
                    id: notifyId,
                    error: true,
                    duration: notifyDuration,
                });
            } finally {
                setIsExporting(false);
            }
        },
        [doExportPpt, pushNotification]
    );

    const cancel = useCallback(() => {
        cancelXls();
        cancelPpt();
    }, [cancelXls, cancelPpt]);

    return {
        isExporting,
        downloadXls,
        downloadPpt,
        cancel,
    };
}
